<template>
	<Popper
		v-if="showSessionViews"
		v-click-outside="closePopover"
		:isVisible="popoverVisible"
		placement="bottom-end"
		@keyup.escape="closePopover"
	>
		<BaseTooltip :text="'Last viewed ' + lastViewAt()" :enabled="!popoverVisible" placement="bottom">
			<button class="flex mb-4 items-center" data-test="session-views-button" @click="togglePopover">
				<BaseUserAvatar
					v-for="(view, index) in last3Views"
					:key="view.anonymousId"
					:url="view.user && view.user.avatar"
					:name="view.user && view.user.name"
					:style="`zIndex: ${3 - index}`"
					:class="[{'-ml-4': index > 0}, 'border border-gray-700']"
				/>
				<span v-if="views.length > 3" class="text-gray-400 text-sm ml-4 inline-block">
					+{{ views.length - 3 }}</span
				>
			</button>
		</BaseTooltip>
		<template #popup>
			<BasePopover style="width: 260px" title="Seen by" data-test="session-views-popover">
				<UserListItem
					v-for="view in sortedViewsCurrentUserAsFirst"
					:key="view.anonymousId"
					:user="view.user"
					class="popover-link cursor-default"
					:class="{'text-gray-100': isUserWorkspaceMember(view.userId)}"
				>
					<template #aside>
						<span class="text-gray-400 flex-grow flex-shrink-0 text-right ml-8 text-sm">
							{{ getRelativeTime(view.createdAt) }}
						</span>
					</template>
				</UserListItem>

				<template v-if="!$store.hasUserBugReporterRole && !$store.hasUserProductViewerRole" #footer>
					<InviteMemberInPopover location="Session Views Popover" />
				</template>
			</BasePopover>
		</template>
	</Popper>
</template>

<script>
import {defineAsyncComponent} from 'vue'

import Popper from 'shared/components/Popper.vue'
import generateBirdName from 'shared/utils/generateBirdName'
import humanDate from 'shared/utils/humanDate'
import sortArrayByObjectProperty from 'shared/utils/sortArrayByObjectProperty'
import {durationByTimeUnits} from 'shared/utils/humanDuration'

export default {
	name: 'SessionViews',
	components: {
		Popper,
		UserListItem: defineAsyncComponent(() => import('shared/components/UserListItem.vue')),
		InviteMemberInPopover: defineAsyncComponent(() => import('shared/components/InviteMemberInPopover.vue')),
	},
	props: {
		views: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			popoverVisible: false,
		}
	},
	computed: {
		showSessionViews() {
			return this.views.length > 1
		},
		sortedViews() {
			const views = this.views.map(this.setUser)
			return sortArrayByObjectProperty(views, 'createdAt').reverse()
		},
		sortedViewsNonCurrentUser() {
			return this.sortedViews.filter(view => view.userId !== this.$store.user?.id)
		},
		sortedViewsCurrentUserAsFirst() {
			const currentUser = this.views.find(view => view.userId === this.$store.user?.id)
			if (!currentUser) return this.sortedViewsNonCurrentUser

			const currentUserAsFirst = [...this.sortedViewsNonCurrentUser]
			currentUserAsFirst.unshift(currentUser)

			return currentUserAsFirst
		},
		last3Views() {
			return this.sortedViewsCurrentUserAsFirst.slice(0, 3)
		},
	},
	methods: {
		getRelativeTime(date, capitalize = true) {
			const commentDate = new Date(date)
			const now = new Date()
			const {days, hours, minutes} = durationByTimeUnits(now - commentDate)
			if (days > 0) {
				return humanDate(commentDate)
			}
			if (hours > 0) {
				return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
			}
			if (minutes > 3) {
				return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`
			}
			return capitalize ? 'Just now' : 'just now'
		},
		lastViewAt() {
			return this.getRelativeTime(this.sortedViewsCurrentUserAsFirst?.[1]?.createdAt, false)
		},
		setUser(view) {
			const user = {...view.user} || {}
			const {userId} = view
			if (!userId || !this.isUserWorkspaceMember(userId)) {
				user.name = generateBirdName()
			}
			if (userId === this.$store.user?.id) {
				user.name = 'You'
			}
			return {...view, user}
		},
		isUserWorkspaceMember(userId) {
			return this.$store.users.some(user => user.id === userId)
		},
		closePopover() {
			this.popoverVisible = false
		},
		togglePopover() {
			if (this.views.length < 2) return
			this.popoverVisible = !this.popoverVisible
		},
	},
}
</script>
